import { yupResolver } from '@hookform/resolvers/yup'
import { LineCheck } from '@qr-front/icons/line-check'
import { LineWifi } from '@qr-front/icons/line-wifi'
import { Checkbox } from '@qr-front/ui/components/Checkbox'
import { fieldLabelCls } from '@qr-front/ui/components/InputField'
import { SelectItem } from '@qr-front/ui/components/Select'
import { PrimitiveAtom, useSetAtom } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, useImperativeHandle } from 'react'
import { FormProvider, Resolver, useForm } from 'react-hook-form'
import { validateContentForm } from 'utils/formUtils'
import { maxLength } from 'utils/inputChangeUtils'
import * as yup from 'yup'
import { InferType } from 'yup'

import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'

import { RhcField, RhcSelectField } from '../components/RhcField'
import { TContentFormHandle, TQrContentFormSection } from './qrContentFormTypes'
import { useSyncFormSectionValidity } from './useSyncFormSectionValidity'

export const WIFI_FORM_ID = 'wifi'
export const WIFI_MAX_LENGTH = 100

export const wifiNameAtom = atomWithReset<string | undefined>('')
export const wifiPasswordAtom = atomWithReset<string | undefined>('')
export const wifiEncryptionAtom = atomWithReset<string | undefined>('WPA')
export const wifiHiddenAtom = atomWithReset<boolean>(false)

export const getWifiSchema = (t: TTranslationFunction) =>
	yup.object({
		ssid: yup
			.string()
			.required(t('ui__field_validation_errors__generic__required'))
			.max(
				WIFI_MAX_LENGTH,
				t('ui__field_validation_errors__generic__max_length', {
					length: WIFI_MAX_LENGTH,
				}),
			),
		password: yup.string().max(
			WIFI_MAX_LENGTH,
			t('ui__field_validation_errors__generic__max_length', {
				length: WIFI_MAX_LENGTH,
			}),
		),
		encryption: yup.string(),
		hidden: yup.boolean(),
	})

export type TWifiFormField = InferType<ReturnType<typeof getWifiSchema>>

export const resetWifiForm = () => {
	getStore().set(wifiNameAtom, RESET)
	getStore().set(wifiPasswordAtom, RESET)
	getStore().set(wifiEncryptionAtom, RESET)
	getStore().set(wifiHiddenAtom, RESET)
}

export const WifiForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()

	const WIFI_ENCRYPTION_OPTIONS = [
		{ label: 'WEP', value: 'WEP' },
		{ label: 'WPA / WPA2', value: 'WPA' },
		{ label: 'WPA2-EAP', value: 'WPA-EAP' },
		{ label: t('generator__create_wifi__nopass'), value: 'nopass' },
	]

	const form = useForm<TWifiFormField>({
		defaultValues: {
			ssid: getStore().get(wifiNameAtom),
			password: getStore().get(wifiPasswordAtom),
			encryption: getStore().get(wifiEncryptionAtom),
			hidden: getStore().get(wifiHiddenAtom),
		},
		mode: 'onTouched',
		reValidateMode: 'onChange',
		resolver: yupResolver(
			getWifiSchema(t),
		) as unknown as Resolver<TWifiFormField>,
		shouldUnregister: false,
	})

	const {
		register,
		formState: { isValid },
		handleSubmit,
	} = form

	const { onChange: onChangeNetworkHidden, ...networkHiddenProps } =
		register('hidden')

	const setWifiHidden = useSetAtom(wifiHiddenAtom)

	useSyncFormSectionValidity({ formId: WIFI_FORM_ID, isValid })

	useImperativeHandle(
		ref,
		() => ({
			validate: () =>
				validateContentForm((onValid, onInvalid) =>
					handleSubmit(onValid, async (errors) => {
						onInvalid(errors)
					}),
				),
		}),
		[handleSubmit],
	)

	return (
		<FormProvider {...form}>
			<form className="flex flex-col gap-4">
				<RhcField
					type="text"
					required
					form={form}
					path="ssid"
					fieldName="wifi_ssid"
					atom={wifiNameAtom as PrimitiveAtom<string>}
					onChangeTransform={(event) =>
						maxLength(WIFI_MAX_LENGTH)(event)
					}
					label={t('generator__step_1__qr_type__wifi_ssid__label')}
					placeholder={t(
						'generator__step_1__qr_type__wifi_ssid__placeholder',
					)}
				/>
				<div className="grid grid-cols-1 gap-x-12 gap-y-4 tablet:grid-cols-2 tablet:gap-y-4">
					<RhcField
						type="text"
						form={form}
						path="password"
						fieldName="wifi_password"
						atom={wifiPasswordAtom}
						onChangeTransform={(event) =>
							maxLength(WIFI_MAX_LENGTH)(event)
						}
						label={t(
							'generator__step_1__qr_type__wifi_password__label',
						)}
						placeholder={t(
							'generator__step_1__qr_type__wifi_password__placeholder',
						)}
						autoComplete="off"
					/>
					<div>
						<label
							htmlFor="wifi-encryption"
							className={fieldLabelCls}
						>
							{t(
								'generator__step_1__qr_type__wifi_encryption__label',
							)}
						</label>
						<RhcSelectField
							form={form}
							path="encryption"
							fieldName="wifi_encryption"
							atom={wifiEncryptionAtom}
						>
							{WIFI_ENCRYPTION_OPTIONS.map(({ label, value }) => (
								<SelectItem
									icon={<LineCheck width={16} height={16} />}
									key={value}
									value={value}
									className="focus:text-dark-grey data-[state=checked]:bg-purple-8 data-[state=checked]:text-dark-grey hover:bg-generator-bg hover:text-dark-grey"
								>
									{label}
								</SelectItem>
							))}
						</RhcSelectField>
					</div>
					<Checkbox
						{...networkHiddenProps}
						id="wifi-checkbox"
						className="tablet:mt-2"
						label={t(
							'generator__step_1__qr_type__wifi_hidden__label',
						)}
						onCheckedChange={(checked) => {
							setWifiHidden(!!checked)
							onChangeNetworkHidden({ target: { checked } })
						}}
					/>
				</div>
			</form>
		</FormProvider>
	)
})

WifiForm.displayName = 'WifiForm'

export const wifiFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: WIFI_FORM_ID,
	title: t('generator__step_1__qr_type_cards__wifi__title'),
	description: t('generator__step_1__qr_type_cards__wifi__subtitle'),
	icon: <LineWifi />,
	Content: WifiForm,
})
